import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import '../../scss/match/editMatchModal.scss';
import api from '../../services/apiService';
import NewLocation from '../venues/NewLocation';
import AddTeamModal from '../AddTeamModal';
import { resolveMatchUpdateStatus } from '../../utils/helpers';
import { fetchYouthOppositionTeams } from '../../utils/oppositionTeams';
import { useNotification } from '../NotificationProvider';

const EditMatchModal = ({ match, onClose, onMatchUpdated }) => {
    const [message, setMessage] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [status, setStatus] = useState('active'); // Always start with 'active' for new updates
    const [alertType, setAlertType] = useState(''); // 'all', 'yes', 'none', empty for required selection
    const [matchDate, setMatchDate] = useState(match.match_date || '');
    const [matchTime, setMatchTime] = useState(match.match_time || '19:30');
    const [location, setLocation] = useState(match.pitch_id || '');
    const [maxPlayers, setMaxPlayers] = useState(match.max_players || 12);
    const [duration, setDuration] = useState(match.duration || 60);
    const [pitches, setPitches] = useState([]);
    const [opposition, setOpposition] = useState(match.opposition || '');
    const [oppositionTeams, setOppositionTeams] = useState([]);
    const [customOpposition, setCustomOpposition] = useState('');
    const [isHome, setIsHome] = useState(match.is_home === 1);
    const teamType = sessionStorage.getItem('selected_team_type');
    const teamId = sessionStorage.getItem('selected_team_id');

    const [statusMessage, setStatusMessage] = useState(''); // Always start with empty message for new updates
    const [responseMessage, setResponseMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [oppositionTeamId, setOppositionTeamId] = useState(match.opposition_team_id || null);
    const [matchType, setMatchType] = useState(match.match_type || 'Friendly');
    const [instructions, setInstructions] = useState(match.instructions || '');
    const [arrivalTimeMinutes, setArrivalTimeMinutes] = useState(match.arrival_time_minutes || 60);
    const [isTBC, setIsTBC] = useState(match.status === 'TBC');
    const [showNewLocation, setShowNewLocation] = useState(false);
    const [showAddTeam, setShowAddTeam] = useState(false);
    const { showSuccess } = useNotification();

    const availabilityMessageSuggestion = 'Please confirm availability, if not already. Thank you.';

    const getStatusMessagePlaceholder = () => {
        if (status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed') {
            return "Please describe what has changed (e.g., 'Location changed to Hastings Sports Centre', 'Time changed to 7:00 PM')";
        }
        if (alertType && alertType !== 'none') {
            return availabilityMessageSuggestion;
        }
        return 'Add a status message...';
    };

    const handleAlertTypeChange = (value) => {
        setAlertType(value);
        if (value === 'awaiting' && !statusMessage.trim()) {
            setStatusMessage(availabilityMessageSuggestion);
        }
    };

    useEffect(() => {
        console.log('EditMatchModal - Initial match data:', {
            opposition: match.opposition,
            opposition_team_id: match.opposition_team_id,
            team_type: match.team_type
        });
        
        const fetchPitches = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_pitches.php`);
                const result = response.data;
                if (result.success) {
                    setPitches(result.pitches);
                }
            } catch (error) {
                console.error('Error fetching pitches:', error);
            }
        };

        const fetchOppositionTeams = async () => {
            if (teamType === 'academy' || teamType === 'squad') {
                try {
                    const teams = await fetchYouthOppositionTeams(teamId);
                    setOppositionTeams(teams);
                    
                    if (match.opposition_team_id) {
                        const oppositionTeam = teams.find(team => String(team.team_id) === String(match.opposition_team_id));
                        if (oppositionTeam) {
                            setOpposition(oppositionTeam.team_name);
                        } else if (match.opposition) {
                            setOppositionTeams(prev => [
                                { team_id: match.opposition_team_id, team_name: match.opposition },
                                ...prev
                            ]);
                            setOpposition(match.opposition);
                        }
                    } else if (match.opposition && !match.opposition_team_id) {
                        setOpposition('other');
                        setCustomOpposition(match.opposition);
                    }
                } catch (error) {
                    console.error('Error fetching opposition teams:', error);
                }
            }
        };

        fetchPitches();
        fetchOppositionTeams();
    }, [teamType, teamId, match.opposition_team_id]);

    // Set the current pitch_id when pitches are loaded
    useEffect(() => {
        if (pitches.length > 0 && match.pitch_id) {
            // Find the current pitch and set the location state
            const currentPitch = pitches.find(pitch => pitch && pitch.pitch_id && pitch.pitch_id === match.pitch_id);
            if (currentPitch) {
                setLocation(match.pitch_id.toString());
            }
        }
    }, [pitches, match.pitch_id]);

    const handleTeamAdded = (newTeam) => {
        setOppositionTeams(prevTeams => {
            const exists = prevTeams.some(team => String(team.team_id) === String(newTeam.team_id));
            return exists ? prevTeams : [...prevTeams, newTeam];
        });
        setOpposition(newTeam.team_name);
        setOppositionTeamId(newTeam.team_id);
        setCustomOpposition('');
        setShowAddTeam(false);
        showSuccess('Team added successfully!', 3000);
    };

    const handleLocationAdded = async (newLocation) => {
        try {
            // Add the new pitch to the pitches array
            const newPitch = {
                pitch_id: newLocation.pitch_id,
                name: newLocation.name,
                address_1: newLocation.address_1,
                address_2: newLocation.address_2 || '',
                town: newLocation.town,
                county: newLocation.county,
                postcode: newLocation.postcode,
                description: newLocation.description || '',
                facilities: newLocation.facilities || '',
                organiser_contact: newLocation.organiser_contact || ''
            };
            
            setPitches(prevPitches => [...prevPitches, newPitch]);
            const pitchId = typeof newLocation.pitch_id === 'string' ? parseInt(newLocation.pitch_id) : newLocation.pitch_id;
            setLocation(pitchId.toString());
            setShowNewLocation(false);
        } catch (error) {
            console.error('Error handling location added:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate that alert type is selected
        if (!alertType) {
            setResponseMessage('Please select an alert option before saving.');
            setShowMessage(true);
            return;
        }
        
        // Validate that status message is provided when alerts are enabled or when time/venue changes
        if ((alertType !== 'none' || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed') && !statusMessage.trim()) {
            setResponseMessage('Please provide an update message when sending alerts to players or when changing time/venue.');
            setShowMessage(true);
            return;
        }
        
        // Note: Removed validation that forced alerts for time/venue changes
        // Users can now select "Don't Alert Anyone" and still save changes
        
        setIsUpdating(true);

        try {
            let finalOppositionTeamId = oppositionTeamId;
            let finalOpposition = opposition;
            
            // Handle custom opposition or opposition from dropdown
            if (teamType !== 'friendly') {
                if (opposition === 'other') {
                    finalOpposition = customOpposition;
                    finalOppositionTeamId = null;
                } else {
                    // Find the selected opposition team ID if a team was selected from the dropdown
                    const selectedTeam = oppositionTeams.find(team => team.team_name === opposition);
                    finalOppositionTeamId = selectedTeam ? selectedTeam.team_id : null;
                }
            }

                        // Find the selected pitch name for the location field
            let pitchName = '';
            
            if (pitches && pitches.length > 0 && location) {
                let selectedPitch;
                
                // First try to find by ID
                if (location && location !== '') {
                    selectedPitch = pitches.find(pitch => pitch && pitch.pitch_id && pitch.pitch_id.toString() === location);
                }
                
                // If not found by ID, try by index (fallback)
                if (!selectedPitch && !isNaN(location)) {
                    const index = parseInt(location);
                    if (index >= 0 && index < pitches.length) {
                        selectedPitch = pitches[index];
                    }
                }
                
                if (selectedPitch) {
                    pitchName = `${selectedPitch.name} - ${selectedPitch.town ?? ''} ${selectedPitch.county ?? ''}`.trim();
                }
            }
            
            // Fallback to current match location if no pitch found
            if (!pitchName && match.location) {
                pitchName = match.location;
            }
            
            const response = await api.post(`${process.env.REACT_APP_API_URL}/update_match.php`, {
                match_id: match.match_id,
                match_date: matchDate,
                match_time: matchTime,
                pitch_id: parseInt(location),  // Convert to integer for database
                location: pitchName,
                max_players: maxPlayers,
                duration,
                arrival_time_minutes: arrivalTimeMinutes,
                opposition: finalOpposition,
                opposition_team_id: finalOppositionTeamId,
                is_home: isHome ? 1 : 0,
                message: statusMessage,
                status: resolveMatchUpdateStatus(isTBC, status),
                alert_type: alertType,
                match_type: (teamType === 'academy' || teamType === 'squad') ? matchType : null,
                instructions,
                isTBC: isTBC
            });

            const result = response.data;
            
            if (result.success) {
                // Find the selected pitch name for the location field
                let updatedLocation = '';
                if (pitches && pitches.length > 0 && location) {
                    let selectedPitch;
                    
                    // First try to find by ID
                    if (location && location !== '') {
                        selectedPitch = pitches.find(pitch => pitch && pitch.pitch_id && pitch.pitch_id.toString() === location);
                    }
                    
                    // If not found by ID, try by index (fallback)
                    if (!selectedPitch && !isNaN(location)) {
                        const index = parseInt(location);
                        if (index >= 0 && index < pitches.length) {
                            selectedPitch = pitches[index];
                        }
                    }
                    
                    if (selectedPitch) {
                        updatedLocation = `${selectedPitch.name} - ${selectedPitch.town ?? ''} ${selectedPitch.county ?? ''}`.trim();
                    }
                }
                
                // Fallback to current match location if no pitch found
                if (!updatedLocation && match.location) {
                    updatedLocation = match.location;
                }
                
                console.log('EditMatchModal - onMatchUpdated called with:', {
                    finalOpposition,
                    finalOppositionTeamId,
                    opposition,
                    customOpposition,
                    teamType
                });
                
                onMatchUpdated({
                    ...match,
                    match_date: matchDate,
                    match_time: matchTime,
                    pitch_id: parseInt(location),  // Convert to integer for database
                    location: updatedLocation,
                    max_players: maxPlayers,
                    duration,
                    arrival_time_minutes: arrivalTimeMinutes,
                    opposition: finalOpposition,
                    opposition_team_id: finalOppositionTeamId, 
                    is_home: isHome,
                    status,
                    last_update_message: statusMessage,
                    match_type: matchType
                });
                setResponseMessage('Match updated successfully');
                setShowMessage(true);
                setTimeout(() => {
                    onClose();
                    window.location.reload(); // Uncommented - page will refresh after successful update
                }, 1500);
            } else {
                setResponseMessage('Failed to update match: ' + result.message);
                setShowMessage(true);
            }
        } catch (error) {
            console.error('Error updating match:', error);
            setResponseMessage('Error updating match. Please try again.');
            setShowMessage(true);
        } finally {
            setIsUpdating(false);
        }
    };

    // Use createPortal to render the modal to document.body
    return createPortal(
        <>
        <div className="modal-overlay EditMatchModal" onClick={onClose}>
            <div className="modal-content modal" onClick={e => e.stopPropagation()}>

                    <h2>Edit Match</h2>
                    <button type="button" className="modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                <form onSubmit={handleSubmit}>
                    {teamType === 'academy' && (
                        <div className="form-group">
                            <label htmlFor="matchType">Match Type:</label>
                            <select
                                id="matchType"
                                value={matchType}
                                onChange={(e) => setMatchType(e.target.value)}
                                disabled={isUpdating}
                            >
                                <option value="Friendly">Friendly</option>
                                <option value="League">League</option>
                                <option value="Cup">Cup</option>
                                <option value="Tournament">Tournament</option>
                            </select>
                        </div>
                    )}
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="matchDate">Match Date:</label>
                            <input
                                type="date"
                                id="matchDate"
                                value={matchDate}
                                onChange={(e) => setMatchDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="matchTime">Start Time:</label>
                            <input
                                type="time"
                                id="matchTime"
                                value={isTBC ? "00:00" : matchTime}
                                onChange={(e) => setMatchTime(e.target.value)}
                                disabled={isTBC}
                                required={!isTBC}
                                style={{ opacity: isTBC ? 0.2 : 1 }}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isTBC}
                                    onChange={(e) => {
                                        setIsTBC(e.target.checked);
                                        if (e.target.checked) {
                                            setMatchTime("00:00");
                                        }
                                    }}
                                />
                                Start Time TBC (To Be Confirmed)
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="label-with-button">
                            <label htmlFor="location">Location:</label>
                            <button 
                                type="button" 
                                className="add-location-button"
                                onClick={() => setShowNewLocation(true)}
                            >
                                + Add New
                            </button>
                        </div>
                        <select
                            id="location"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                        >
                            <option value="">Select Location</option>
                            {pitches.map((pitch, index) => (
                                <option key={pitch?.pitch_id || index} value={pitch?.pitch_id || index}>
                                    {pitch?.name || 'Unknown'} - {pitch?.town ?? ''} {pitch?.county ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="duration">Duration (mins):</label>
                            <input
                                type="number"
                                id="duration"
                                value={duration}
                                onChange={(e) => setDuration(parseInt(e.target.value))}
                                min="1"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="arrivalTimeMinutes">Arrival Time (mins before kick-off):</label>
                            <input
                                type="number"
                                id="arrivalTimeMinutes"
                                value={arrivalTimeMinutes}
                                onChange={(e) => setArrivalTimeMinutes(parseInt(e.target.value))}
                                min="0"
                                max="180"
                            />
                        </div>
                        {teamType !== 'academy' && (
                            <div className="form-group">
                                <label htmlFor="maxPlayers">Max Players:</label>
                                <input
                                    type="number"
                                    id="maxPlayers"
                                    value={maxPlayers}
                                    onChange={(e) => setMaxPlayers(parseInt(e.target.value))}
                                    min="1"
                                    required
                                />
                            </div>
                        )}
                    </div>

                    {(teamType === 'squad' || teamType === 'academy') && (
                        <>
                            <div className="form-group">
                                <div className="label-with-button">
                                    <label htmlFor="opposition">Opposition:</label>
                                    <button
                                        type="button"
                                        className="add-team-button"
                                        onClick={() => setShowAddTeam(true)}
                                    >
                                        + Add Team
                                    </button>
                                </div>
                                {oppositionTeams.length > 0 ? (
                                    <select
                                        id="opposition"
                                        value={opposition}
                                        onChange={(e) => setOpposition(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Select opposition team --</option>
                                        {oppositionTeams.map(team => (
                                            <option key={team.team_id} value={team.team_name}>
                                                {team.team_name}
                                                {team.age_group ? ` ${team.age_group}` : ''}
                                                {team.manager_name ? ` (${team.manager_name})` : ''}
                                            </option>
                                        ))}
                                        <option value="other">Other (not listed)</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        id="opposition"
                                        value={opposition}
                                        onChange={(e) => setOpposition(e.target.value)}
                                        placeholder="Enter opposition team name"
                                        required
                                    />
                                )}
                                {opposition === 'other' && (
                                    <input
                                        type="text"
                                        id="custom-opposition"
                                        value={customOpposition}
                                        onChange={(e) => setCustomOpposition(e.target.value)}
                                        placeholder="Enter custom team name"
                                        required
                                        style={{ marginTop: '10px' }}
                                    />
                                )}
                            </div>
                            <div className="form-group">
                                <label>Match Location:</label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="isHome"
                                            checked={isHome === true}
                                            onChange={() => setIsHome(true)}
                                            required
                                        />
                                        Home
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="isHome"
                                            checked={isHome === false}
                                            onChange={() => setIsHome(false)}
                                            required
                                        />
                                        Away
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label htmlFor="instructions">Match Instructions:</label>
                        <textarea
                            id="instructions"
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            placeholder="Add match instructions or notes..."
                            rows="4"
                        />
                    </div>

                    <div className="notification-section" style={{
                        marginTop: '2rem',
                        padding: '1.5rem',
                        backgroundColor: 'rgba(10, 179, 216, 0.1)',
                        border: '1px solid rgba(10, 179, 216, 0.3)',
                        borderRadius: '8px',
                        borderLeft: '4px solid #0ab3d8'
                    }}>
                        <h3 className="section-heading" style={{
                            margin: '0 0 0.5rem 0',
                            color: '#0ab3d8',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>Notification Settings</h3>
                        <p className="section-description" style={{
                            margin: '0 0 1.5rem 0',
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.9rem',
                            fontStyle: 'italic'
                        }}>Configure how to notify players about these changes</p>
                        
                        <div className="form-group">
                            <label>Status:</label>
                            <select 
                                value={status} 
                                onChange={(e) => setStatus(e.target.value)}
                                disabled={isUpdating}
                            >
                                <option value="active">Active - Match is on!</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="postponed">Postponed</option>
                                <option value="time_changed">Change of Time</option>
                                <option value="venue_changed">Change of Venue</option>
                                <option value="venue_time_changed">Venue & Time Changed</option>
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="statusMessage">
                                Update Message {(alertType && alertType !== 'none') || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed' ? '(required)' : '(optional)'}:
                            </label>
                            <textarea
                                id="statusMessage"
                                value={statusMessage}
                                onChange={(e) => setStatusMessage(e.target.value)}
                                placeholder={getStatusMessagePlaceholder()}
                                rows="4"
                                required={(alertType && alertType !== 'none') || status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed'}
                            />
                        </div>

                        <div className="form-group">
                            <label>Alert Players:</label>
                            <select 
                                value={alertType} 
                                onChange={(e) => handleAlertTypeChange(e.target.value)}
                                required
                            >
                                <option value="">-- Please Select --</option>
                                <option value="none">Don't Alert Anyone</option>
                                <option value="awaiting">Alert Players Awaiting Response Only</option>
                                <option value="yes">Alert Players Attending</option>
                                <option value="yes_awaiting">Alert Players Attending + Awaiting</option>
                                <option value="all">Alert All Invited Players</option>
                            </select>
                            {alertType === 'awaiting' && (
                                <small style={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontStyle: 'italic',
                                    marginTop: '0.5rem',
                                    display: 'block'
                                }}>
                                    Only parents/players who have not responded yet will be notified.
                                </small>
                            )}
                            {(status === 'time_changed' || status === 'venue_changed' || status === 'venue_time_changed') && (
                                <small style={{ 
                                    color: '#ffc107', 
                                    fontStyle: 'italic', 
                                    marginTop: '0.5rem',
                                    display: 'block'
                                }}>
                                    💡 Tip: Time and venue changes are important updates. Consider alerting all invited players.
                                </small>
                            )}
                        </div>
                    </div>

                    <div className="form-navigation">
                        <button type="button" onClick={onClose} className="back-stage-button">
                            Cancel
                        </button>
                        <button type="submit" className="next-stage-button" disabled={isUpdating}>
                            {isUpdating ? 'Updating...' : 'Save Changes'}
                        </button>
                    </div>
                </form>

                {showMessage && (
                    <div className="response-message">
                        {responseMessage}
                    </div>
                )}

                {showNewLocation && (
                    <NewLocation 
                        onClose={() => setShowNewLocation(false)}
                        onLocationAdded={handleLocationAdded}
                    />
                )}
            </div>
        </div>
        {showAddTeam && (
            <AddTeamModal
                onClose={() => setShowAddTeam(false)}
                onTeamAdded={handleTeamAdded}
                teamType={teamType}
                currentTeam={{
                    team_id: teamId,
                    team_name: sessionStorage.getItem('selected_team_name'),
                    age_group: sessionStorage.getItem('selected_team_age_group'),
                    league_name: sessionStorage.getItem('selected_team_league')
                }}
            />
        )}
        </>,
        document.body
    );
};

export default EditMatchModal; 